import clsx from 'clsx';
import { ActionButton, Note, Outcome, Step } from 'components';
import { useAnalytics } from 'contexts/Analytics/Analytics';
import { useCalculator } from 'contexts/Calculator/Calculator';
import { useEffect, useRef, useState } from 'react';
import Graph from './Graph';
import PdfTable from './PdfTable';
import Setup from './Setup';
import Variant from './Variant';

export default function Analyze() {

    const [breakable, setBreakable] = useState(false);
    const [layout, setLayout] = useState<{ horizontal: boolean; containerStyles: any; listStyles: any }>({
        horizontal: false,
        containerStyles: null,
        listStyles: null,
    });
    const analytics = useAnalytics();
    const calculator = useCalculator();
    const wrapRef = useRef<any>(null);
    const isAnalysisReady =
        calculator.variants.length >= 2 &&
        !!calculator.comparisonResult?.evaluation &&
        !!calculator.experimentResult;
    const wasAnalysisReadyRef = useRef(isAnalysisReady);
    const skipInitialReadyEventRef = useRef(typeof window !== 'undefined' && !!window.location.hash);

    const variantWidth = 320;
    const margin = 10;
    const spacing = 25;

    useEffect(() => {
        window.addEventListener("load", handleResize);
        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("load", handleResize);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        const isReady =
            calculator.variants.length >= 2 &&
            !!calculator.comparisonResult?.evaluation &&
            !!calculator.experimentResult;
        const wasReady = wasAnalysisReadyRef.current;

        if (skipInitialReadyEventRef.current && wasReady && isReady) {
            skipInitialReadyEventRef.current = false;
        }

        if (!wasReady && isReady) {
            if (skipInitialReadyEventRef.current) {
                skipInitialReadyEventRef.current = false;
            } else {
                void analytics.trackActionEvent('analysis_result_ready', {
                    analysis_evaluation: calculator.comparisonResult.evaluation,
                    analysis_result_label: calculator.experimentResult,
                    resource_id: 'variant_result',
                    resource_type: 'tool',
                    source: 'analyze_step',
                    variant_count: calculator.variants.length,
                });
            }
        }

        wasAnalysisReadyRef.current = isReady;
    }, [analytics, calculator.comparisonResult?.evaluation, calculator.experimentResult, calculator.variants.length]);

    useEffect(() => {
        const newHorizontal = breakable && calculator.variants.length <= 2;
        setLayout({
            horizontal: newHorizontal,
            containerStyles: {
                width: newHorizontal ? ((variantWidth + spacing) * Math.max(1, calculator.variants.length) - spacing + margin * 2) : undefined,
                overflowX: calculator.variants.length > 2 ? 'auto' : 'visible',
            },
            listStyles: {
                width: (variantWidth + spacing) * calculator.variants.length - spacing + margin * 2,
            },
        });
    }, [breakable, calculator.variants.length]);

    function handleResize() {
        const rect = wrapRef.current?.getBoundingClientRect();
        if (rect) {
            const newBreakable = rect.width / (variantWidth + spacing) >= 3;
            setBreakable(newBreakable);
        }
    }

    const variantsClasses = clsx("pdf-hide", {
        "d-flex": layout.horizontal
    });

    const resetButtonDisabled = !calculator.variants.some((variant) => variant.result || variant.sampleSize) && calculator.variants.length === 1;

    const hasResultsToShow = Array.isArray(calculator.results) && calculator.results.some((r) => r != null);

    const note = calculator.comparisonResult.evaluation === "INCONCLUSIVE" && "Your result is inconclusive. To identify a Winner, try increasing the sample Size.";

    return (
        <Step description="Understand if you reached your goal, or which version of a variant is better." exportTitle="Result Analysis" ordinal={2} title="Analyze the Results" onOpen={handleResize}>
            <Setup />
            <PdfTable />
            <div className={variantsClasses} ref={wrapRef}>
                <div className="variant-container" style={layout.containerStyles}>
                    <div className="variant-list" style={layout.listStyles}>
                        {calculator.variants.map((variant, index) => (
                            <Variant key={index} {...variant} />
                        ))}
                    </div>
                </div>
                {hasResultsToShow && (
                    <div className={layout.horizontal ? 'd-flex flex-column flex-grow-1 overflow-hidden pb-2 ps-5 pt-4' : 'my-5'}>
                        <Graph
                            className={layout.horizontal ? 'flex-grow-1' : 'pdf-hide'}
                            height={layout.horizontal ? undefined : 400}
                        />
                    </div>
                )}
            </div>
            <div className="my-5">
                <div>
                    {calculator.variants.length > 1 && (
                        <div className="text-center d-lg-inline-block mb-3 text-lg-start pdf-align-left">
                            <Outcome className="d-inline-block" highlight={!!calculator.comparisonResult.evaluation} title="Variant Result">
                                <span data-testid="variant-result-text">{calculator.experimentResult}</span>
                            </Outcome>
                        </div>
                    )}
                    {note && (
                        <Note className="my-5 text-center d-lg-none">{note}</Note>
                    )}
                    <div className="text-center d-lg-inline-block ms-lg-5 text-lg-start pdf-hide">
                        <ActionButton
                            aria-label="Add new variant"
                            className="mx-3 my-1 mx-lg-0 my-lg-0"
                            data-testid="analyze-add-variant"
                            icon="add"
                            label="Add New Variant"
                            onClick={calculator.addVariant}
                        />
                        <ActionButton
                            aria-label="Reset all variants"
                            className="mx-3 my-1 mx-lg-0 my-lg-0 ms-lg-4"
                            data-testid="analyze-reset-variants"
                            disabled={resetButtonDisabled}
                            icon="reset-variants"
                            label="Reset All Variants"
                            onClick={calculator.resetVariants}
                        />
                    </div>
                </div>
                {note && (
                    <Note className="d-none mt-2 d-lg-block">{note}</Note>
                )}
            </div>
        </Step>
    );
}
